import { useCallback, useRef } from 'react'

// Voice respond is on api.romea.ai (meet server), not the OpenClaw gateway
const CLAW_URL = 'https://api.romea.ai'

export interface ClawResponseEvent {
  type: 'text-delta' | 'tool-call' | 'tool-result' | 'confirm-required' | 'complete' | 'error'
  data: any
}

export function useClawVoice() {
  const abortRef = useRef<AbortController | null>(null)

  const respond = useCallback(async (
    workspace: string,
    meetingId: string,
    mode: string,
    utterance: string,
    context: { lastTurns: string; meetingTitle?: string },
    onEvent: (e: ClawResponseEvent) => void,
  ) => {
    if (abortRef.current) {
      abortRef.current.abort()
    }
    abortRef.current = new AbortController()

    try {
      const resp = await fetch(`${CLAW_URL}/voice/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace,
          meetingId,
          mode,
          utterance,
          context,
        }),
        signal: abortRef.current.signal,
      })

      if (!resp.ok || !resp.body) {
        onEvent({ type: 'error', data: `HTTP ${resp.status}` })
        return
      }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            const eventType = line.slice(7).trim()
            // Next line should be data:
            continue
          }
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6)
            try {
              const data = JSON.parse(dataStr)
              onEvent({ type: data.type || 'text-delta', data })
            } catch {
              onEvent({ type: 'text-delta', data: { text: dataStr } })
            }
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        onEvent({ type: 'error', data: String(err) })
      }
    }
  }, [])

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
  }, [])

  return { respond, cancel }
}
